import React, { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useDesignStore } from '../../store/designStore';
import { zoomCanvasKeepingCenter } from '../../hooks/useCanvasZoom';
import type { FabricCanvas } from '../../types/fabric';

const ZOOM_PRESETS = [10, 25, 50, 75, 100, 150, 200];

interface BottomBarProps {
  canvas: FabricCanvas | null;
  onFitToViewport: () => void;
  /** The workspace's scroll container — kept centered on whatever's currently
   * visible when zooming via these buttons. See `useCanvasZoom.ts`. */
  containerRef?: RefObject<HTMLDivElement | null>;
}

export function BottomBar({ canvas, onFitToViewport, containerRef }: BottomBarProps) {
  const { t } = useTranslation();
  const { selectedObject, showGrid, toggleGrid, showRulers, toggleRulers } = useDesignStore();
  // Initialise from the canvas immediately if it already has a zoom set (e.g. after fitToViewport)
  const [zoom, setZoom] = useState(() => canvas?.getZoom() ?? 1);
  const [showZoomMenu, setShowZoomMenu] = useState(false);

  const [, tick] = useState(0);

  useEffect(() => {
    if (!canvas) return;
    // Sync once on mount in case canvas was already zoomed before this component rendered
    setZoom(canvas.getZoom());
    const update = () => {
      setZoom(canvas.getZoom());
      // `setZoom` alone is a no-op re-render when the zoom value itself hasn't
      // changed (e.g. rotating an object via PropertyPanel's slider/number input
      // calls canvas.renderAll(), which fires this same 'after:render' — but with
      // an unchanged zoom, React bails out and the ↻ angle readout below goes
      // stale instead of tracking the rotation that just happened).
      tick(n => n + 1);
    };
    canvas.on('after:render', update);
    return () => {
      canvas.off('after:render', update);
    };
  }, [canvas]);

  const applyZoom = (nz: number) => {
    if (!canvas) return;
    // Keeps whatever's currently centered in the scroll container centered
    // after the zoom, instead of always resetting the scroll position back
    // to the top-left corner on every single zoom step.
    const applied = zoomCanvasKeepingCenter(canvas, nz, containerRef?.current ?? null);
    setZoom(applied);
  };

  const zoomIn = () => {
    if (!canvas) return;
    applyZoom(Math.min(canvas.getZoom() * 1.2, 10));
  };

  const zoomOut = () => {
    if (!canvas) return;
    applyZoom(Math.max(canvas.getZoom() / 1.2, 0.05));
  };

  const applyZoomPreset = (pct: number) => {
    applyZoom(pct / 100);
    setShowZoomMenu(false);
  };

  // Shown in the same (-180, 180] convention as PropertyPanel's rotation
  // field (see its comment) rather than Fabric's raw [0, 360) angle — keeps
  // this HUD readout agreeing with the editable field instead of showing,
  // e.g., "270°" here while PropertyPanel shows "-90°" for the same object.
  const rawAngle = selectedObject ? Math.round(((selectedObject as any).angle ?? 0) % 360 + 360) % 360 : null;
  const angle = rawAngle === null ? null : (rawAngle > 180 ? rawAngle - 360 : rawAngle);

  return (
    <div style={{
      height: 34,
      background: '#ffffff',
      borderTop: '1px solid #e2e8f0',
      display: 'flex',
      alignItems: 'center',
      padding: '0 12px',
      gap: 6,
      fontSize: 12,
      color: '#64748b',
      flexShrink: 0,
      position: 'relative',
    }}>
      {angle !== null && (
        <>
          <span style={{ fontSize: 12 }}>↻ {angle}°</span>
          <div style={sepStyle} />
        </>
      )}

      <button onClick={zoomOut} style={barBtn} title={t('toolbar.zoomOut')}>🔍−</button>

      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setShowZoomMenu(v => !v)}
          style={{ ...barBtn, minWidth: 60, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 6px' }}
        >
          {(zoom * 100).toFixed(1)}%
        </button>
        {showZoomMenu && (
          <div style={{
            position: 'absolute', bottom: '110%', left: '50%', transform: 'translateX(-50%)',
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '4px 0', zIndex: 100,
            minWidth: 80,
          }}>
            {ZOOM_PRESETS.map(pct => (
              <button key={pct} onClick={() => applyZoomPreset(pct)}
                style={{
                  display: 'block', width: '100%', padding: '5px 12px',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 12,
                  textAlign: 'right', color: '#374151',
                }}>
                {pct}%
              </button>
            ))}
          </div>
        )}
      </div>

      <button onClick={zoomIn} style={barBtn} title={t('toolbar.zoomIn')}>🔍+</button>
      <button onClick={onFitToViewport} style={{ ...barBtn, fontSize: 14, fontWeight: 600 }} title={t('toolbar.fitToViewport')}>⤢</button>

      <div style={sepStyle} />
      <button
        onClick={toggleGrid}
        style={{ ...barBtn, color: showGrid ? 'var(--primary)' : '#374151', fontWeight: showGrid ? 700 : 400 }}
        title={t('toolbar.toggleGrid')}
      >
        ▦
      </button>
      <button
        onClick={toggleRulers}
        style={{ ...barBtn, color: showRulers ? 'var(--primary)' : '#374151', fontWeight: showRulers ? 700 : 400 }}
        title={t('toolbar.toggleRulers')}
      >
        📏
      </button>

      {showZoomMenu && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setShowZoomMenu(false)} />
      )}
    </div>
  );
}

const barBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '2px 4px',
  borderRadius: 4,
  fontSize: 14,
  color: '#374151',
};

const sepStyle: React.CSSProperties = {
  width: 1,
  height: 14,
  background: '#e2e8f0',
};
